<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { InfiniteScroll } from '@inertiajs/vue3'
import { usePage } from '@inertiajs/vue3'
import PostCard from '@/Pages/Post/Partials/PostCard.vue'
import Ad from '@/Pages/Ads/Ad.vue'
import { useWheelNavigation } from '@/Composables/useWheelNavigation'
import { useTouchNavigation } from '@/Composables/useTouchNavigation'
import { useVirtualPosts } from '@/Composables/useVirtualPosts'
import { FEED_CONSTANTS, RESOURCE_TYPE } from '@/Shared/feedConstants'

const props = defineProps({
    posts: { type: Object, required: true },
    current: { type: Number, default: 0 },
    preserveUrl: { type: Boolean, default: false },
    buffer: { type: Number, default: 2000 },
})

const emit = defineEmits([
    'post-enter',
    'post-active',
    'post-leave',
    'posts-loaded'
])

const settings = computed(() => usePage().props.settings)
const feedContainer = ref(null)
const containerHeight = ref(0)
const scrollLocked = ref(false)
const nextFetch = ref(null)
const prevFetch = ref(null)
const isMounted = ref(false)

const postsWithAds = computed(() => {
    if (!settings.value.ads_enabled) return props.posts.data

    const result = []
    const interval = settings.value.ads_interval

    props.posts.data.forEach((post, index) => {
        result.push(post)

        // Insert ad after every N posts
        if ((index + 1) % interval === 0) {
            result.push({
                resource: RESOURCE_TYPE.AD,
                uuid: `ad-after-${index}`,
            })
        }
    })

    return result
})

// Get initial index - no ads inserted, so just return current
const getInitialIndex = () => {
    return props.current
}

const { currentIndex, virtualPosts, navigateToPost } = useVirtualPosts(
    postsWithAds,
    emit,
    scrollLocked,
    { nextFetch, prevFetch },
    computed(() => props.posts),
    getInitialIndex()
)

const { handleWheel } = useWheelNavigation(navigateToPost, scrollLocked)

const {
    isDragging,
    dragDelta,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
} = useTouchNavigation(navigateToPost, scrollLocked, containerHeight)

const getTransform = (position) => {
    if (!containerHeight.value) return 'translateY(0)'

    const offsets = {
        prev: -containerHeight.value,
        current: 0,
        next: containerHeight.value
    }

    const totalOffset = offsets[position] + dragDelta.value
    return `translateY(${totalOffset}px)`
}

const getTransition = () => {
    return (isMounted.value && !isDragging.value)
        ? `transform ${FEED_CONSTANTS.TRANSITION_DURATION_MS}ms ${FEED_CONSTANTS.TRANSITION_EASING}`
        : 'none'
}

const handleAdPlaying = (playing) => {
    scrollLocked.value = playing
}

const handleAdSkip = () => {
    scrollLocked.value = false
    navigateToPost('next')
}

watch(() => props.posts.data, (newPosts, oldPosts) => {
    if (oldPosts && newPosts.length > oldPosts.length) {
        const loadedPosts = newPosts.slice(oldPosts.length)
        emit('posts-loaded', loadedPosts)
    }
}, { deep: true })

onMounted(() => {
    if (feedContainer.value) {
        containerHeight.value = feedContainer.value.clientHeight

        feedContainer.value.addEventListener('wheel', handleWheel, { passive: false })
        feedContainer.value.addEventListener('touchstart', handleTouchStart, { passive: true })
        feedContainer.value.addEventListener('touchmove', handleTouchMove, { passive: false })
        feedContainer.value.addEventListener('touchend', handleTouchEnd, { passive: true })
    }

    nextTick(() => {
        const currentPost = postsWithAds.value[currentIndex.value]
        if (currentPost) {
            emit('post-active', currentPost, currentIndex.value)
            emit('post-enter', currentPost, currentIndex.value)
        }

        setTimeout(() => {
            isMounted.value = true
        }, FEED_CONSTANTS.MOUNT_TRANSITION_DELAY_MS)
    })
})

onUnmounted(() => {
    if (feedContainer.value) {
        feedContainer.value.removeEventListener('wheel', handleWheel)
        feedContainer.value.removeEventListener('touchstart', handleTouchStart)
        feedContainer.value.removeEventListener('touchmove', handleTouchMove)
        feedContainer.value.removeEventListener('touchend', handleTouchEnd)
    }
})

defineExpose({
    lockScroll: () => handleAdPlaying(true),
    unlockScroll: () => handleAdPlaying(false),
})
</script>

<template>
    <div
        ref="feedContainer"
        class="relative w-full overflow-hidden overscroll-y-contain"
        style="height: calc(100dvh - 3.5rem);"
    >
        <div
            v-for="{ post, index, position } in virtualPosts"
            :key="post.uuid"
            class="absolute top-0 bottom-0 left-0 right-0 will-change-transform"
            :style="{
                transform: getTransform(position),
                transition: getTransition()
            }"
        >
            <slot
                v-if="$slots.default"
                :post="post"
                :index="index"
                :is-next="index === currentIndex + 1"
                :is-prev="index === currentIndex - 1"
            />
            <template v-else>
                <PostCard
                    v-if="post.resource === RESOURCE_TYPE.POST"
                    :post="post"
                    :index="index"
                    :is-next="index === currentIndex + 1"
                    :is-prev="index === currentIndex - 1"
                    @enter="emit('post-enter', post, index)"
                    @active="emit('post-active', post, index)"
                    @leave="emit('post-leave', post, index)"
                    @ad-playing="handleAdPlaying"
                />
                <Ad
                    v-else
                    :is-current="position === 'current'"
                    @enter="emit('post-enter', post, index)"
                    @active="emit('post-active', post, index)"
                    @leave="emit('post-leave', post, index)"
                    @ad-playing="handleAdPlaying"
                    @skip="handleAdSkip"
                />
            </template>
        </div>

        <InfiniteScroll
            data="posts"
            manual
            :preserve-url="preserveUrl"
        >
            <template #next="{ fetch }">
                <div v-if="fetch && !nextFetch">{{ (nextFetch = fetch) && '' }}</div>
            </template>

            <template #previous="{ fetch }">
                <div v-if="fetch && !prevFetch">{{ (prevFetch = fetch) && '' }}</div>
            </template>
        </InfiniteScroll>
    </div>
</template>
